/**
 * Тип опций для настройки CookieManager.
 */
export type CookieModalConfig = {
	selectors?: {
		applyButton?: string, // Селектор кнопки применения выбранных настроек
		applyButtonText?: string, // Селектор текста кнопки применения
		cancelButton?: string, // Селектор кнопки отмены
		settingsButton?: string, // Селектор кнопки открытия настроек
		mainContainer?: string, // Селектор основного контейнера модального окна
		settingsContainer?: string // Селектор контейнера с настройками
	},
	stateClasses?: {
		activeCookie: string, // Класс, активирующий отображение модального окна
		hiddenContainer: string // Класс, скрывающий контейнер
	},
	onCloseAction: 'refuse' | 'apply' | false, // Действие при закрытии модального окна: 'refuse' - отклонить все, 'apply' - применить все, false
}

/**
 * Класс CookieModal — управление модальным окном для согласия с использованием cookies.
 * Реализован как Singleton (единственный экземпляр).
 */
export class CookieModal {
	static _instance;

	isApply = false;

	onCloseAction = false;

	// Селекторы по умолчанию
	selectors = {
		applyButton: '.js-cookie-apply',
		applyAllButton: '.js-cookie-apply-all',
		closeButton: '.js-cookie-close',
		settingsButton: '.js-cookie-settings',
		mainContainer: '.cookie__main',
		settingsContainer: '.cookie__settings',
	};

	// CSS-классы состояния
	stateClasses = {
		activeCookie: 'cookie--active',
		hiddenContainer: 'cookie__container--hidden',
	};

	/**
	 * Конструктор класса
	 * @param {HTMLElement} target — целевой элемент (контейнер модального окна)
	 * @param {CookieModalConfig} options — опции для переопределения селекторов и классов
	 */
	constructor(target, options :CookieModalConfig = {}) {
		if (CookieModal._instance) {
			console.warn('Class CookieModal — is singleton, instance already exists');
			return CookieModal._instance;
		}
		CookieModal._instance = this;

		if (options.selectors) {
			this.selectors = {
				...this.selectors,
				...options.selectors
			};
		}

		if (options.stateClasses) {
			this.stateClasses = {
				...this.stateClasses,
				...options.stateClasses
			}
		}

		if (options.onCloseAction) {
			this.onCloseAction = options.onCloseAction;
		}

		this.target = target;
		this.applyButton = this.target.querySelector(this.selectors.applyButton);
		this.applyAllButton = this.target.querySelector(this.selectors.applyAllButton);
		this.closeButtons = this.target.querySelectorAll(this.selectors.closeButton);
		this.settingsButton = this.target.querySelector(this.selectors.settingsButton);

		this.mainContainer = this.target.querySelector(this.selectors.mainContainer);
		this.settingsContainer = this.target.querySelector(this.selectors.settingsContainer);

		this.bindEvents();
	}

	/**
	 * Открывает модальное окно cookie
	 */
	open() {
		this.target.classList.add(this.stateClasses.activeCookie);
		this.showMain();
	}

	/**
	 * Закрывает модальное окно cookie
	 */
	close = () => {
		if (!this.isApply && this.onCloseAction !== false) {
			const settings = {};
			this.settingsContainer?.querySelectorAll('input[type="checkbox"]').forEach((el) => {

				if (this.onCloseAction === 'apply' || this.onCloseAction === 'refuse') {
					if (this.onCloseAction === 'refuse') {
						settings[el.name] = !!el.disabled;
					} else {
						settings[el.name] = true;
					}

					this.isApply = true;
					this.target.dispatchEvent(new CustomEvent('apply', { detail: { settings: settings } }));
				}
			});
		}

		this.target.classList.remove(this.stateClasses.activeCookie);
	};

	/**
	 * Показывает основной экран (с кратким сообщением и кнопками)
	 */
	showMain() {
		if (this.mainContainer) this.mainContainer.classList.remove(this.stateClasses.hiddenContainer);
		if (this.settingsContainer) this.settingsContainer.classList.add(this.stateClasses.hiddenContainer);
	}

	/**
	 * Применяет выбранные настройки cookie
	 * @param {boolean} all — если true, применяются все настройки (все чекбоксы = true)
	 */
	applySettings(all = false) {
		const settings = {};
		this.settingsContainer?.querySelectorAll('input[type="checkbox"]').forEach((el) => {
			if (all) settings[el.name] = true;
			else settings[el.name] = el.checked;
		});
		
		// Генерация события с данными о настройках
		this.target.dispatchEvent(new CustomEvent('apply', { detail: { settings: settings } }));
		this.isApply = true;

		this.close();
	}
	
	/**
	 * Показывает экран с настройками cookie
	 */
	showSettings() {
		if (this.mainContainer) this.mainContainer.classList.add(this.stateClasses.hiddenContainer);
		if (this.settingsContainer) this.settingsContainer.classList.remove(this.stateClasses.hiddenContainer);
	}

	/**
	 * Назначает обработчики событий на кнопки
	 */
	bindEvents() {
		this.closeButtons.length > 0 && this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
		this.settingsButton?.addEventListener('click', () => this.showSettings());
		this.applyButton?.addEventListener('click', () => this.applySettings());
		this.applyAllButton?.addEventListener('click', () => this.applySettings(true));
	}
}